import { useState } from 'react';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Card, Button, Modal, Input, Select, Badge } from '../components/ui';
import useServicesStore, { Service, ServiceVariant } from '../stores/servicesStore';

const SERVICE_OPTIONS = [
	'Haircut',
	'Haircut & Style',
	'Blowout',
	'Full Color',
	'Root Touch-Up',
	'Highlights',
	'Balayage',
	'Ombre',
	'Keratin Treatment',
	'Deep Conditioning Treatment',
	'Hair Extension',
	'Perm',
	'Relaxer',
	'Braiding',
	'Updo',
	'Bridal Hair',
	'Trim',
	'Bang Trim',
	'Scalp Treatment',
	'Texture Service',
	'Color Correction',
	'Toner',
	'Gloss Treatment',
];

const SIZE_OPTIONS = ['Jumbo', 'XL', 'L', 'M', 'SM', 'S', 'XS', 'XXS'];
const LENGTH_OPTIONS = ['Shoulder', 'Bra', 'Mid-Back', 'Waist', 'Butt', 'Hip', 'Knee'];

const Services = () => {
	const { services, addService, updateService, deleteService } = useServicesStore();
	const [searchTerm, setSearchTerm] = useState('');
	const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set());
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingService, setEditingService] = useState<Service | null>(null);

	// Form state
	const [serviceName, setServiceName] = useState('');
	const [tempVariants, setTempVariants] = useState<ServiceVariant[]>([]);
	const [currentVariant, setCurrentVariant] = useState<ServiceVariant>({
		size: '',
		length: '',
		duration: 0,
		price: 0,
	});

	const filteredServices = services.filter(service => service.name.toLowerCase().includes(searchTerm.toLowerCase()));

	const toggleExpanded = (id: string) => {
		const newExpanded = new Set(expandedServices);
		if (newExpanded.has(id)) {
			newExpanded.delete(id);
		} else {
			newExpanded.add(id);
		}
		setExpandedServices(newExpanded);
	};

	const openAddModal = () => {
		setEditingService(null);
		setServiceName('');
		setTempVariants([]);
		setCurrentVariant({ size: '', length: '', duration: 0, price: 0 });
		setIsModalOpen(true);
	};

	const openEditModal = (service: Service) => {
		setEditingService(service);
		setServiceName(service.name);
		setTempVariants([...service.variants]);
		setCurrentVariant({ size: '', length: '', duration: 0, price: 0 });
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setEditingService(null);
		setServiceName('');
		setTempVariants([]);
		setCurrentVariant({ size: '', length: '', duration: 0, price: 0 });
	};

	const addVariantToList = () => {
		if (!currentVariant.size || !currentVariant.length || !currentVariant.duration || !currentVariant.price) {
			toast.error('Please fill all variant fields');
			return;
		}

		setTempVariants([...tempVariants, { ...currentVariant, id: `temp-${Date.now()}` }]);
		setCurrentVariant({ size: '', length: '', duration: 0, price: 0 });
		toast.success('Variant added');
	};

	const removeVariant = (index: number) => {
		setTempVariants(tempVariants.filter((_, i) => i !== index));
	};

	const saveService = () => {
		if (!serviceName) {
			toast.error('Please select a service name');
			return;
		}

		if (tempVariants.length === 0) {
			toast.error('Please add at least one size variant');
			return;
		}

		const serviceData: Service = {
			id: editingService?.id || `service-${Date.now()}`,
			name: serviceName,
			variants: tempVariants,
		};

		if (editingService) {
			updateService(editingService.id, serviceData);
			toast.success('Service updated successfully!');
		} else {
			addService(serviceData);
			toast.success('Service added successfully!');
		}

		closeModal();
	};

	const handleDeleteService = (id: string, name: string) => {
		if (confirm(`Are you sure you want to delete "${name}" and all its variants?`)) {
			deleteService(id);
			toast.success('Service deleted');
		}
	};

	return (
		<AppLayout
			topbarContent={
				<div className="flex items-center gap-3 w-full max-w-md">
					<div className="flex-1 flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
						<span>🔍</span>
						<input
							type="text"
							placeholder="Search services..."
							value={searchTerm}
							onChange={e => setSearchTerm(e.target.value)}
							className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
						/>
					</div>
				</div>
			}
		>
			{/* Header */}
			<Card className="mb-5">
				<div className="flex justify-between items-start mb-4">
					<div>
						<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Services Management</h1>
						<p className="text-sm text-gray-600 dark:text-gray-400">Add, view, edit, and manage salon services with multiple size variants</p>
					</div>
					<Button variant="primary" onClick={openAddModal}>
						+ Add New Service
					</Button>
				</div>
			</Card>

			{/* Services List */}
			<Card>
				{filteredServices.length === 0 ? (
					<div className="text-center py-12">
						<div className="text-6xl mb-4 opacity-30">💅</div>
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
							{searchTerm ? 'No services found' : 'No services yet'}
						</h3>
						<p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
							{searchTerm ? 'Try a different search term' : 'Get started by adding your first service'}
						</p>
						{!searchTerm && (
							<Button variant="primary" onClick={openAddModal}>
								+ Add First Service
							</Button>
						)}
					</div>
				) : (
					<div className="space-y-4">
						{filteredServices.map(service => (
							<div key={service.id} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 hover:shadow-md transition-shadow">
								<div
									className="p-4 flex justify-between items-center cursor-pointer"
									onClick={() => toggleExpanded(service.id)}
								>
									<div className="flex items-center gap-3">
										<span className="font-semibold text-lg text-gray-900 dark:text-gray-100">{service.name}</span>
										<span className="text-sm text-gray-500 dark:text-gray-400">
											({service.variants.length} size{service.variants.length > 1 ? 's' : ''})
										</span>
									</div>
									<div className="flex items-center gap-2">
										<Button
											size="sm"
											variant="secondary"
											onClick={e => {
												e.stopPropagation();
												openEditModal(service);
											}}
										>
											✏️ Edit
										</Button>
										<Button
											size="sm"
											variant="danger"
											onClick={e => {
												e.stopPropagation();
												handleDeleteService(service.id, service.name);
											}}
										>
											🗑️ Delete
										</Button>
										<span
											className={`text-sm transition-transform ${expandedServices.has(service.id) ? 'rotate-180' : ''}`}
										>
											▼
										</span>
									</div>
								</div>

								{expandedServices.has(service.id) && (
									<div className="p-4 pt-0 border-t border-gray-200 dark:border-gray-600">
										<div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Available Sizes & Pricing:</div>
										<div className="space-y-2">
											{service.variants.map((variant, idx) => (
												<div
													key={idx}
													className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-4 items-center p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500"
												>
													<Badge variant="primary">{variant.size}</Badge>
													<Badge variant="info">{variant.length}</Badge>
													<span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{variant.duration} mins</span>
													<span className="font-semibold text-sm sm:text-base text-green-600 dark:text-green-400">${variant.price.toFixed(2)}</span>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						))}
					</div>
				)}
			</Card>

			<div className="mt-6 text-sm text-gray-500 dark:text-gray-400 text-center">© Beauty Business Portal</div>

			{/* Add/Edit Service Modal */}
			<Modal
				isOpen={isModalOpen}
				onClose={closeModal}
				title={editingService ? 'Edit Service' : 'Add New Service'}
				size="2xl"
			>
				<div className="space-y-6">
					{/* Service Name */}
					<Select
						label="Service Name"
						value={serviceName}
						onChange={e => setServiceName(e.target.value)}
						required
					>
						<option value="">Select Service</option>
						{SERVICE_OPTIONS.map(name => (
							<option key={name} value={name}>
								{name}
							</option>
						))}
					</Select>

					{/* Variants Section */}
					<div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
						<div className="flex justify-between items-center mb-4">
							<h4 className="font-semibold text-gray-900 dark:text-gray-100">Size Variants</h4>
						</div>

						{/* Add Variant Form */}
						<div className="grid grid-cols-4 gap-3 mb-4">
							<Select
								value={currentVariant.size}
								onChange={e => setCurrentVariant({ ...currentVariant, size: e.target.value })}
							>
								<option value="">Size</option>
								{SIZE_OPTIONS.map(size => (
									<option key={size} value={size}>
										{size}
									</option>
								))}
							</Select>

							<Select
								value={currentVariant.length}
								onChange={e => setCurrentVariant({ ...currentVariant, length: e.target.value })}
							>
								<option value="">Length</option>
								{LENGTH_OPTIONS.map(length => (
									<option key={length} value={length}>
										{length}
									</option>
								))}
							</Select>

							<Input
								type="number"
								placeholder="Duration (mins)"
								min={5}
								step={5}
								value={currentVariant.duration || ''}
								onChange={e => setCurrentVariant({ ...currentVariant, duration: parseInt(e.target.value) || 0 })}
							/>

							<Input
								type="number"
								placeholder="Price"
								min={0}
								step={0.01}
								value={currentVariant.price || ''}
								onChange={e => setCurrentVariant({ ...currentVariant, price: parseFloat(e.target.value) || 0 })}
							/>
						</div>

						<Button variant="success" size="sm" onClick={addVariantToList} className="w-full">
							✓ Add Variant
						</Button>

						{/* Added Variants List */}
						{tempVariants.length > 0 && (
							<div className="mt-4">
								<div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Added Variants:</div>
								<div className="space-y-2">
									{tempVariants.map((variant, idx) => (
										<div
											key={idx}
											className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 items-center p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500"
										>
											<Badge variant="primary">{variant.size}</Badge>
											<Badge variant="info">{variant.length}</Badge>
											<span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{variant.duration} mins</span>
											<span className="font-semibold text-sm sm:text-base text-green-600 dark:text-green-400">${variant.price.toFixed(2)}</span>
											<Button size="sm" variant="danger" onClick={() => removeVariant(idx)} className="col-span-2 sm:col-span-1">
												Remove
											</Button>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Action Buttons */}
					<div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t">
						<Button variant="secondary" onClick={closeModal} className="w-full sm:w-auto">
							Cancel
						</Button>
						<Button variant="success" onClick={saveService} className="w-full sm:w-auto">
							💾 {editingService ? 'Update' : 'Save'} Service
						</Button>
					</div>
				</div>
			</Modal>
		</AppLayout>
	);
};

export default Services;
